import { FunctionComponent, useEffect, useState } from "react";
import { Checkbox, Select } from "antd";
import { useTranslation } from "react-i18next";
import { UserOffice } from "@type/core/auth/auth.types";
import { Office, OfficesPreferences } from "@type/core/offices/offices.types";
import {
  requestDeleteAutomaticRelaunch,
  requestUpdateOfficesPreferences,
} from "@state/offices/OfficesEffects";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";

const MINIMUM_DELAY = 1;
const MAXIMUM_DELAY = 14;

interface AutomaticRelaunchTabProps {
  office: UserOffice;
  getDefaultValue: (
    office: UserOffice,
    preference: string,
    defaultValue?: boolean,
  ) => boolean;
  checkboxEvent: (
    office: UserOffice,
    isChecked: boolean,
    preferenceKey: string,
  ) => void;
  preferences: OfficesPreferences | undefined;
}

const AutomaticRelaunchTab: FunctionComponent<AutomaticRelaunchTabProps> = ({
  office,
  getDefaultValue,
  checkboxEvent,
  preferences,
}) => {
  const { t } = useTranslation();

  const getAutomaticRelaunchDefaultValue = (
    office: Office,
    preferenceKey: string,
  ): number => {
    if (preferences) {
      for (const preference of preferences.officePreferences) {
        if (preference.id === office.id) {
          for (const defaultValue of preference.preferences) {
            if (defaultValue.preferenceKey === preferenceKey) {
              return parseInt(defaultValue.stringValue, 10);
            }
          }
        }
      }
    }
    return 0;
  };

  const [automaticRelaunchCheckBoxValue, setAutomaticRelaunchCheckBoxValue] =
    useState<boolean>(
      !!getAutomaticRelaunchDefaultValue(office, "automaticRelaunch"),
    );
  const [days, setDays] = useState("1");
  const [disabledSelect, setDisabledSelect] = useState<boolean>(
    !getAutomaticRelaunchDefaultValue(office, "automaticRelaunch"),
  );

  useEffect(() => {
    const isChecked = !!getAutomaticRelaunchDefaultValue(
      office,
      "automaticRelaunch",
    );
    setAutomaticRelaunchCheckBoxValue(isChecked);
    setDisabledSelect(!isChecked);
  }, [preferences]);

  const getRelanceOptions = () => {
    return Array.from(
      Array(MAXIMUM_DELAY - MINIMUM_DELAY + 1).keys(),
      (delay) => delay + MINIMUM_DELAY,
    );
  };

  const relaunchCheckBoxEvent = (office: Office, isChecked: boolean) => {
    if (!isChecked) {
      void requestDeleteAutomaticRelaunch({
        dto: undefined,
        id: "",
        upperEntityId: office.id,
      })
        .then((result) => {
          if (result.ok && result.data) {
            toastSuccess(t("offices.preferences.errors.checkboxSuccess"));
          }
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.checkboxError"));
        });
    } else {
      void requestUpdateOfficesPreferences({
        dto: {
          preferenceKey: "automaticRelaunch",
          htmlValue: null,
          stringValue: days,
        },
        upperEntityId: office.id,
      })
        .then((result) => {
          if (result.ok && result.data) {
            toastSuccess(t("offices.preferences.errors.checkboxSuccess"));
          }
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.checkboxError"));
        });
    }
  };

  const relaunchSelectEvent = (
    value: number,
    office: Office,
    preferenceKey: string,
  ) => {
    if (automaticRelaunchCheckBoxValue) {
      void requestUpdateOfficesPreferences({
        dto: {
          preferenceKey: preferenceKey,
          htmlValue: null,
          stringValue: value.toString(),
        },
        upperEntityId: office.id,
      })
        .then((result) => {
          if (result.ok && result.data) {
            toastSuccess(t("offices.preferences.errors.relaunchSuccess"));
          }
        })
        .catch(() => {
          toastError(t("offices.preferences.errors.relaunchError"));
        });
    }
  };

  return (
    <>
      <div className="mb-16">
        <i>{t("offices.preferences.automaticRelaunch.subtitle")}</i>
      </div>
      <Checkbox
        className="mb-8"
        checked={automaticRelaunchCheckBoxValue}
        onChange={(e) => {
          setAutomaticRelaunchCheckBoxValue(e.target.checked);
          setDisabledSelect(!e.target.checked);
          relaunchCheckBoxEvent(office, (e.target as HTMLInputElement).checked);
        }}
      >
        {t(
          "offices.preferences.automaticRelaunch.checkbox.isAutomaticRelaunchActive",
        )}
      </Checkbox>
      <Select
        defaultValue={
          getAutomaticRelaunchDefaultValue(office, "automaticRelaunch") || 1
        }
        onChange={(value) => {
          setDays(value.toString());
          relaunchSelectEvent(value, office, "automaticRelaunch");
        }}
        disabled={disabledSelect}
        className="w-100"
      >
        {getRelanceOptions().map((option) => (
          <Select.Option key={option} value={option}>
            {t("offices.preferences.automaticRelaunch.relaunchPeriod", {
              period: option === 1 ? "" : `${option} `,
            })}
          </Select.Option>
        ))}
      </Select>
      <div className="mt-24 mb-16">
        <i>{t("offices.preferences.notDownloadedDocumentRecap.description")}</i>
      </div>
      <Checkbox
        defaultChecked={getDefaultValue(office, "notDownloadedDocument", true)}
        onChange={(e) =>
          checkboxEvent(
            office,
            (e.target as HTMLInputElement).checked,
            "notDownloadedDocument",
          )
        }
      >
        {t(
          "offices.preferences.notDownloadedDocumentRecap.checkbox.isNotDownloadedDocumentRecap",
        )}
      </Checkbox>
    </>
  );
};

export default AutomaticRelaunchTab;
